import { useRef, useEffect, forwardRef, useState } from 'react';
import {
    useTable,
    useSortBy,
    usePagination,
    useRowSelect,
    useGlobalFilter,
    useAsyncDebounce,
    useExpanded,
} from 'react-table';
import { useDispatch } from 'react-redux';
import { setSelectedRows } from 'redux/selectedRows';
import classNames from 'classnames';
import { Pagination } from './Pagination';
import { Button } from 'react-bootstrap';
import { GlobalFilterProps, IndeterminateCheckboxProps, TableProps } from '@models/components/table';

const GlobalFilter = ({ preGlobalFilteredRows, globalFilter, setGlobalFilter, searchBoxClass }: GlobalFilterProps) => {
    const count = preGlobalFilteredRows.length;
    const [value, setValue] = useState<any>(globalFilter);
    const onChange = useAsyncDebounce((value) => {
        setGlobalFilter(value || undefined);
    }, 200);

    return (
        <div className={classNames(searchBoxClass)}>
            <span className="d-flex align-items-center weik-text-grey-500">
                <i className="mdi mdi-card-search me-1 h3"></i> Buscar:
                <input
                    value={value || ''}
                    onChange={(e) => {
                        setValue(e.target.value);
                        onChange(e.target.value);
                    }}
                    placeholder={`${count} registros...`}
                    className="form-control w-100 ms-1"
                />
            </span>
        </div>
    );
};

const IndeterminateCheckbox = forwardRef<HTMLInputElement, IndeterminateCheckboxProps>(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = useRef();
        const resolvedRef: any = ref ?? defaultRef;

        useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return (
            <div className="form-check">
                <input type="checkbox" className="form-check-input" ref={resolvedRef} {...rest} />
                <label htmlFor="form-check-input" className="form-check-label"></label>
            </div>
        );
    }
);

const Table = <TableValues extends object = {}>(props: TableProps<TableValues>) => {
    const isSearchable = props['isSearchable'] ?? false;
    const isSortable = props['isSortable'] ?? false;
    const pagination = props['pagination'] ?? false;
    const isSelectable = props['isSelectable'] ?? false;
    const isExpandable = props['isExpandable'] ?? false;
    const sizePerPageList = props['sizePerPageList'] ?? [];

    let otherProps: any = {};

    if (isSearchable) {
        otherProps['useGlobalFilter'] = useGlobalFilter;
    }
    if (isSortable) {
        otherProps['useSortBy'] = useSortBy;
    }
    if (isExpandable) {
        otherProps['useExpanded'] = useExpanded;
    }
    if (pagination) {
        otherProps['usePagination'] = usePagination;
    }
    if (isSelectable) {
        otherProps['useRowSelect'] = useRowSelect;
    }
    const dispatch = useDispatch();

    const dataTable = useTable(
        {
            columns: props['columns'],
            data: props['data'],
            initialState: {
                pageSize: props['pageSize'] ?? 10,
                ...(props['sortBy'] ? { sortBy: props['sortBy'] } : {}),
            },
            autoResetPage: false,
            autoResetExpanded: false,
            selectSubRows: false,
        },

        otherProps.hasOwnProperty('useGlobalFilter') && otherProps['useGlobalFilter'],
        otherProps.hasOwnProperty('useSortBy') && otherProps['useSortBy'],
        otherProps.hasOwnProperty('useExpanded') && otherProps['useExpanded'],
        otherProps.hasOwnProperty('usePagination') && otherProps['usePagination'],
        otherProps.hasOwnProperty('useRowSelect') && otherProps['useRowSelect'],

        (hooks) => {
            isSelectable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllPageRowsSelectedProps }: any) => (
                            <div>
                                <IndeterminateCheckbox {...getToggleAllPageRowsSelectedProps()} />
                            </div>
                        ),
                        // The cell can use the individual row's getToggleRowSelectedProps method
                        // to the render a checkbox
                        Cell: ({ row }: any) => (
                            <div>
                                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                            </div>
                        ),
                    },
                    ...columns,
                ]);

            isExpandable &&
                hooks.visibleColumns.push((columns) => [
                    // Let's make a column for selection
                    {
                        // Build our expander column
                        id: 'expander', // Make sure it has an ID
                        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
                            <Button
                                {...getToggleAllRowsExpandedProps()}
                                variant={isAllRowsExpanded ? 'light' : 'dark'}
                                className="btn-icon p-1">
                                {isAllRowsExpanded ? (
                                    <i className={classNames('mdi', 'mdi-arrow-expand-up')}></i>
                                ) : (
                                    <i className={classNames('mdi', 'mdi-arrow-expand-down')}></i>
                                )}
                            </Button>
                        ),
                        Cell: ({ row }) =>
                            // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
                            // to build the toggle for expanding a row
                            row.canExpand ? (
                                <Button
                                    {...row.getToggleRowExpandedProps({
                                        style: {
                                            // We can even use the row.depth property
                                            // and paddingLeft to indicate the depth
                                            // of the row
                                            paddingLeft: `${row.depth * 2}rem`,
                                        },
                                    })}
                                    variant={row.isExpanded ? 'light' : 'dark'}
                                    className="btn-icon p-1">
                                    {row.isExpanded ? (
                                        <i className={classNames('mdi', 'mdi-wrench-outline')}></i>
                                    ) : (
                                        <i className={classNames('mdi', 'mdi-wrench')}></i>
                                    )}
                                </Button>
                            ) : null,
                    },
                    ...columns,
                ]);
        }
    );

    useEffect(() => {
        if (dataTable && isExpandable && dataTable.toggleAllRowsExpanded) {
            dataTable.toggleAllRowsExpanded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (dataTable.selectedFlatRows) {
            const selectedRows: {}[] = dataTable.selectedFlatRows.map((row) => row.original);
            dispatch(setSelectedRows(selectedRows));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataTable.selectedFlatRows]);

    let rows = pagination ? dataTable.page : dataTable.rows;

    return (
        <>
            {isSearchable && (
                <GlobalFilter
                    preGlobalFilteredRows={dataTable.preGlobalFilteredRows}
                    globalFilter={dataTable.state.globalFilter}
                    setGlobalFilter={dataTable.setGlobalFilter}
                    searchBoxClass={props['searchBoxClass']}
                />
            )}

            <div className="table-responsive table-custom-font-size">
                <table
                    {...dataTable.getTableProps()}
                    className={classNames('table table-centered react-table', props['tableClass'])}>
                    <thead
                        className={`weik-text-grey-500 fw-bold text-uppercase ${
                            props['theadClass'] ? props['theadClass'] : ''
                        }`}>
                        {dataTable.headerGroups.map((headerGroup, index) => (
                            <tr key={index}>
                                {headerGroup.headers.map((column: any, index) => (
                                    <th
                                        {...column.getHeaderProps(
                                            isSortable && column.defaultCanSort && column.getSortByToggleProps()
                                        )}
                                        key={index}
                                        className={
                                            isSortable
                                                ? `${classNames({
                                                      sorting_desc: column.isSortedDesc === true,
                                                      sorting_asc: column.isSortedDesc === false,
                                                      sortable: column.defaultCanSort === true,
                                                  })} ${column.render('Header')}
                                                  text-uppercase`
                                                : ''
                                        }>
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...dataTable.getTableBodyProps()} className="weik-text-grey-500">
                        {(rows || []).map((row, index) => {
                            dataTable.prepareRow(row);
                            return (
                                <tr {...row.getRowProps()} key={index}>
                                    {row.cells.map((cell, index) => {
                                        return (
                                            <td {...cell.getCellProps()} key={index}>
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {pagination && <Pagination tableProps={dataTable} sizePerPageList={sizePerPageList} />}
        </>
    );
};

export { Table };
